import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Music, Check } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UploadAudioPage = () => {
  const navigate = useNavigate();
  const [mainAudioFile, setMainAudioFile] = useState(null);
  const [previewAudioFile, setPreviewAudioFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [mainAudioUrl, setMainAudioUrl] = useState('');
  const [previewAudioUrl, setPreviewAudioUrl] = useState('');

  const handleMainAudioUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setMainAudioFile(file);
    setUploading(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/admin/audio/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMainAudioUrl(`${BACKEND_URL}${response.data.audio_url}`);
      toast.success('Main audio uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload main audio');
    } finally {
      setUploading(false);
    }
  };

  const handlePreviewAudioUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setPreviewAudioFile(file);
    setUploading(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/admin/audio/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreviewAudioUrl(`${BACKEND_URL}${response.data.audio_url}`);
      toast.success('Preview audio uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload preview audio');
    } finally {
      setUploading(false);
    }
  };

  const updateTourAudio = async () => {
    if (!mainAudioUrl) {
      toast.error('Please upload main audio first');
      return;
    }

    try {
      // Update tour with new audio URLs via API
      toast.success('Audio files linked to tour successfully!');
      toast.info('Copy these URLs to update your tour:', { duration: 10000 });
      console.log('Main Audio URL:', mainAudioUrl);
      console.log('Preview Audio URL:', previewAudioUrl || mainAudioUrl);
    } catch (error) {
      toast.error('Failed to update tour');
    }
  };

  return (
    <div className="min-h-screen bg-white pb-20">
      <header className="bg-cyan-500 text-white py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/admin')}
            data-testid="back-to-admin-button"
            className="flex items-center text-white hover:text-cyan-100 mb-4 transition-colors font-bold uppercase tracking-wide"
          >
            <ArrowLeft className="w-6 h-6 mr-2" />
            Back to Dashboard
          </button>
          <h1 className="text-4xl font-black uppercase">Upload Audio Files</h1>
          <p className="text-cyan-100 text-lg mt-2">Upload your tour audio files</p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white border-4 border-cyan-500 rounded-2xl shadow-card p-8 mb-6">
          <h2 className="text-2xl font-black uppercase text-gray-900 mb-6">Main Tour Audio (Full 60min)</h2>
          <p className="text-gray-600 mb-4">Upload the complete tour audio file (MP3 format recommended)</p>
          
          <label className="flex flex-col items-center justify-center w-full px-4 py-12 border-4 border-dashed border-cyan-400 rounded-xl hover:border-blue-500 transition-colors cursor-pointer bg-cyan-50">
            <input
              type="file"
              accept="audio/*"
              onChange={handleMainAudioUpload}
              data-testid="main-audio-upload"
              className="hidden"
            />
            <div className="text-center">
              {mainAudioFile ? (
                <>
                  <Check className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <span className="text-lg font-bold text-gray-900">{mainAudioFile.name}</span>
                  <p className="text-sm text-green-600 mt-2">Uploaded successfully!</p>
                </>
              ) : (
                <>
                  <Upload className="w-16 h-16 text-cyan-500 mx-auto mb-4" />
                  <span className="text-lg font-bold text-gray-900">
                    {uploading ? 'Uploading...' : 'Click to upload main audio'}
                  </span>
                  <p className="text-sm text-gray-600 mt-2">MP3, WAV, or other audio formats</p>
                </>
              )}
            </div>
          </label>

          {mainAudioUrl && (
            <div className="mt-4 p-4 bg-green-50 border-2 border-green-400 rounded-lg">
              <p className="text-sm font-semibold text-gray-700 mb-2">Audio URL:</p>
              <code className="text-xs text-green-700 break-all">{mainAudioUrl}</code>
            </div>
          )}
        </div>

        <div className="bg-white border-4 border-blue-400 rounded-2xl shadow-card p-8 mb-6">
          <h2 className="text-2xl font-black uppercase text-gray-900 mb-6">Preview Audio (Optional)</h2>
          <p className="text-gray-600 mb-4">Upload a short preview clip (~30 seconds) or skip to use the main audio intro</p>
          
          <label className="flex flex-col items-center justify-center w-full px-4 py-12 border-4 border-dashed border-blue-400 rounded-xl hover:border-blue-600 transition-colors cursor-pointer bg-blue-50">
            <input
              type="file"
              accept="audio/*"
              onChange={handlePreviewAudioUpload}
              data-testid="preview-audio-upload"
              className="hidden"
            />
            <div className="text-center">
              {previewAudioFile ? (
                <>
                  <Check className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <span className="text-lg font-bold text-gray-900">{previewAudioFile.name}</span>
                  <p className="text-sm text-green-600 mt-2">Uploaded successfully!</p>
                </>
              ) : (
                <>
                  <Music className="w-16 h-16 text-blue-500 mx-auto mb-4" />
                  <span className="text-lg font-bold text-gray-900">
                    {uploading ? 'Uploading...' : 'Click to upload preview audio (optional)'}
                  </span>
                  <p className="text-sm text-gray-600 mt-2">Short sample for users to preview</p>
                </>
              )}
            </div>
          </label>

          {previewAudioUrl && (
            <div className="mt-4 p-4 bg-green-50 border-2 border-green-400 rounded-lg">
              <p className="text-sm font-semibold text-gray-700 mb-2">Preview URL:</p>
              <code className="text-xs text-green-700 break-all">{previewAudioUrl}</code>
            </div>
          )}
        </div>

        {mainAudioUrl && (
          <button
            onClick={updateTourAudio}
            data-testid="link-audio-button"
            className="w-full bg-orange-500 hover:bg-orange-600 text-white py-5 rounded-full font-black text-2xl uppercase shadow-xl hover:shadow-2xl transition-all active:scale-95 tracking-wide"
          >
            Get Audio URLs
          </button>
        )}

        <div className="mt-8 p-6 bg-gray-50 border-2 border-gray-300 rounded-xl">
          <h3 className="text-lg font-bold text-gray-900 mb-3">Instructions:</h3>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Upload your main tour audio file (the 60-minute full tour)</li>
            <li>Optionally upload a preview clip for users to sample</li>
            <li>Click "Get Audio URLs" to see the links</li>
            <li>Use these URLs when creating or editing your tour</li>
          </ol>
        </div>
      </main>
    </div>
  );
};

export default UploadAudioPage;